// =============================================================================
// Fase 5.5 — Wizard RCA (orchestrator)
// Coordina i 7 step. Gestisce navigazione, validazione step-by-step, submit.
// =============================================================================
import React, { useMemo, useState, useCallback, useEffect } from "react";
import { ChevronLeft, ChevronRight, CheckCircle2, PartyPopper, AlertTriangle } from "lucide-react";
import { useRcaWizard } from "../../hooks/useRcaWizard";
import { trackWizardStep } from "@/lib/analytics";
import AccountCredentialsBox from "../../components/wizard/AccountCredentialsBox";
import { Step1SoggettoRichiesta } from "./Step1SoggettoRichiesta";
import { Step2TipoRichiesta } from "./Step2TipoRichiesta";
import { Step3Documenti } from "./Step3Documenti";
import { Step3bCambioVeicolo } from "./Step3bCambioVeicolo";
import { Step4Veicolo } from "./Step4Veicolo";
import { Step5Bersani } from "./Step5Bersani";
import { StepGaranzie } from "./StepGaranzie";
import { Step6Contatto } from "./Step6Contatto";
import { Step7Riepilogo } from "./Step7Riepilogo";

/**
 * Percorsi:
 *   - contatto: 1 → 2 → 6(minimal) → 7 (3 step logici)
 *   - rinnovo/fermo: 1 → 2 → 3 → 4 → 6 → 7 (6 step logici)
 *   - nuova: 1 → 2 → 3 → 4 → 5 → 6 → 7 (7 step logici)
 *   - cambio_veicolo (cambio):               1 → 2 → 3b → garanzie → 6 → 7
 *   - cambio_veicolo (vendita_rottamazione): 1 → 2 → 3b → 6 → 7 (no garanzie)
 */
export function RcaWizard({ partner, sede, onDone, onBackToTipo }) {
  const wiz = useRcaWizard({ partner, sede });
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState("");
  const [confirmed, setConfirmed] = useState(null);

  // FASE K1 — Nuovo ordine: prima "Cosa vuoi fare" (step2_tipo), poi
  // "Chi è l'interlocutore" (step1_soggetto). Se tipo=cambio_veicolo,
  // soggetto è assunto = dipendente_titolare → SKIP dello step soggetto.
  const stepPipeline = useMemo(() => {
    const s = ["step2_tipo"];
    // Include lo step soggetto SOLO se il tipo NON è cambio_veicolo
    // (chi cambia/vende è per definizione il titolare della convenzione).
    if (wiz.state.tipo_richiesta !== "cambio_veicolo") {
      s.push("step1_soggetto");
    }
    if (wiz.state.tipo_richiesta === "contatto") {
      s.push("step6_contatto", "step7_riepilogo");
    } else if (wiz.state.tipo_richiesta === "cambio_veicolo") {
      s.push("step3b_cambio_veicolo");
      if (wiz.state.sub_operazione === "cambio") s.push("step_garanzie");
      s.push("step6_contatto", "step7_riepilogo");
    } else if (wiz.state.tipo_richiesta === "rinnovo" || wiz.state.tipo_richiesta === "fermo") {
      s.push("step3_documenti", "step4_veicolo", "step_garanzie",
              "step6_contatto", "step7_riepilogo");
    } else if (wiz.state.tipo_richiesta === "nuova") {
      s.push("step3_documenti", "step4_veicolo", "step5_bersani",
              "step_garanzie", "step6_contatto", "step7_riepilogo");
    }
    return s;
  }, [wiz.state.tipo_richiesta, wiz.state.sub_operazione]);

  const totalLogicalSteps = stepPipeline.length || 2;
  const currentStepKey = stepPipeline[wiz.step - 1] || "step2_tipo";

  // K1 — Auto-set soggetto_richiesta = "dipendente" (titolare della convenzione)
  // quando l'utente sceglie cambio_veicolo (lo step "interlocutore" è saltato).
  // BUG FIX K1.1: usare `wiz.setField` (non `wiz.update`, inesistente) e
  // il valore "dipendente" (valido lato backend: RCA_VALID_SOGGETTO).
  React.useEffect(() => {
    if (wiz.state.tipo_richiesta === "cambio_veicolo"
        && wiz.state.soggetto_richiesta !== "dipendente") {
      wiz.setField("soggetto_richiesta", "dipendente");
    }
  }, [wiz.state.tipo_richiesta]);  // eslint-disable-line react-hooks/exhaustive-deps

  // Analytics: emit step view ad ogni cambio step
  useEffect(() => {
    try {
      trackWizardStep({
        ramo: "rca",
        step_number: wiz.step,
        step_name: currentStepKey,
        partner_id: partner?.id,
      });
    } catch { /* ignore */ }
  }, [currentStepKey, wiz.step, partner?.id]);

  // Validazione per abilitare "Avanti"
  const canGoNext = useCallback(() => {
    const s = wiz.state;
    if (currentStepKey === "step1_soggetto") return !!s.soggetto_richiesta;
    if (currentStepKey === "step2_tipo") {
      if (!s.tipo_richiesta) return false;
      // FASE A: sub_operazione obbligatoria se tipo=cambio_veicolo
      if (s.tipo_richiesta === "cambio_veicolo" && !s.sub_operazione) return false;
      return true;
    }
    if (currentStepKey === "step3b_cambio_veicolo") {
      // Targa vecchio veicolo obbligatoria + validata
      const t = (s.targa_veicolo_cambiato || "").toUpperCase();
      if (!/^[A-Z]{2}\d{3}[A-Z]{2}$/.test(t)) return false;
      return wiz.missingRequiredDocs.length === 0;
    }
    if (currentStepKey === "step3_documenti") {
      // Familiare titolare info completa se soggetto=familiare
      if (s.soggetto_richiesta === "familiare") {
        const t = s.dipendente_titolare;
        if (!t.nome || !t.cognome || !t.parentela) return false;
        if (!/^[A-Z0-9]{11,16}$/.test((t.codice_fiscale || "").toUpperCase())) return false;
      }
      // Almeno tutti i doc "required" caricati (via missingRequiredDocs=0)
      return wiz.missingRequiredDocs.length === 0;
    }
    if (currentStepKey === "step4_veicolo") {
      return !!s.veicolo.targa && !!s.veicolo.tipo_veicolo;
    }
    if (currentStepKey === "step5_bersani") {
      // v1.75.2 — Se tipo_richiesta="nuova", prima la nuova domanda dedicata.
      if (s.tipo_richiesta === "nuova" && !s.veicolo_ha_classe_merito) return false;
      if (!s.rc_familiare) return false;
      if (s.rc_familiare !== "si") return true; // no/non_so → ok
      // rc_familiare=si → devono essere risposte le sotto-domande
      if (s.sinistri_5y_donante === true) return false; // warning aperto, non può proseguire senza decidere
      if (s.sinistri_5y_donante !== false) return false;
      if (!s.intestatario_donante) return false;
      const d = s.donante_info;
      if (!d.nome || !d.cognome || !d.targa) return false;
      if (s.intestatario_donante === "familiare" && !d.parentela) return false;
      return wiz.missingRequiredDocs.length === 0;
    }
    if (currentStepKey === "step_garanzie") {
      // Fase 5.6 — sempre OK (RCA garantita); Avanti abilitato di default.
      return Array.isArray(s.garanzie_interesse) && s.garanzie_interesse.includes("rca");
    }
    if (currentStepKey === "step6_contatto") {
      const c = s.contatto;
      if (!c.nome || !c.cognome) return false;
      if (!/^[+\d\s]{6,30}$/.test(c.telefono || "")) return false;
      const canale = s.contatto_preferito;
      if (!canale) return false;
      if (s.tipo_richiesta !== "contatto") {
        // Valida email se canale=email, valida CF contraente
        if (canale === "email" && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(c.email || "")) return false;
        if (!/^[A-Z0-9]{11,16}$/.test((c.codice_fiscale || "").toUpperCase())) return false;
      }
      if (canale === "chiamata") {
        if (!s.contatto_dettagli.giorno || !s.contatto_dettagli.fascia_oraria) return false;
      }
      return true;
    }
    return true;
  }, [wiz, currentStepKey]);

  const goNext = () => {
    if (!canGoNext()) return;
    if (currentStepKey === "step7_riepilogo") return;
    wiz.setStep(wiz.step + 1);
    window.scrollTo({ top: 0, behavior: "smooth" });
  };
  const goBack = () => {
    if (wiz.step <= 1) {
      onBackToTipo?.();
      return;
    }
    wiz.setStep(wiz.step - 1);
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const handleSubmit = async () => {
    setSubmitError("");
    setSubmitting(true);
    try {
      const res = await wiz.submitLead();
      setConfirmed(res);
      onDone?.(res);
      window.scrollTo({ top: 0, behavior: "smooth" });
    } catch (e) {
      const detail = e?.response?.data?.detail;
      setSubmitError(
        typeof detail === "string" ? detail
        : Array.isArray(detail) ? detail.map((d) => d.msg || String(d)).join("; ")
        : "Invio fallito. Riprova o contatta l'agenzia."
      );
    } finally {
      setSubmitting(false);
    }
  };

  if (confirmed) {
    const canale = wiz.state.contatto_preferito;
    const msg =
      canale === "whatsapp"
        ? "Grazie, la tua richiesta è stata inviata. Verrai contattato il prima possibile tramite WhatsApp da un esperto della nostra agenzia."
        : canale === "chiamata"
        ? "Grazie, la tua richiesta è stata inviata. Faremo il possibile per contattarti nel giorno e nella fascia oraria indicati."
        : "Grazie, la tua richiesta è stata inviata correttamente. Un esperto di Colucci Assicuratori analizzerà la documentazione e ti ricontatterà il prima possibile.";
    return (
      <div className="rounded-2xl border-2 border-emerald-400 bg-emerald-50 p-6 text-center space-y-3" data-testid="rca-conferma">
        <PartyPopper className="w-10 h-10 text-emerald-600 mx-auto" />
        <h2 className="text-xl font-semibold text-emerald-900">Richiesta inviata!</h2>
        <p className="text-sm text-emerald-900 max-w-xl mx-auto">{msg}</p>
        {confirmed.numero_pratica && (
          <p className="text-xs text-emerald-800">
            Numero pratica: <strong>{confirmed.numero_pratica}</strong>
          </p>
        )}
        {/* FASE K2 — box credenziali (visibile solo se opt-in + account creato) */}
        <AccountCredentialsBox account={confirmed.account_cliente} />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="rca-wizard">
      {/* Sub-progress interna */}
      <div>
        <div className="flex items-center gap-1.5 mb-1.5">
          {Array.from({ length: totalLogicalSteps }).map((_, i) => (
            <div
              key={i}
              className={`h-1 flex-1 rounded-full transition-all ${
                i < wiz.step ? "bg-[var(--brand-accent,#163321)]" : "bg-[#E5E2DB]"
              }`}
            />
          ))}
        </div>
        <p className="text-[10px] text-[#4A5E51] uppercase tracking-wider">
          Passo {wiz.step} di {totalLogicalSteps} · RCA
        </p>
      </div>

      {/* Header partner badge */}
      <div
        className="p-3 bg-[#163321]/5 border border-[#163321]/20 rounded-lg flex items-center gap-2 text-xs"
        style={{ borderLeft: "4px solid var(--brand-accent, #163321)" }}
        data-testid="rca-banner-partner"
      >
        <CheckCircle2 className="w-4 h-4 text-[var(--brand-accent,#163321)]" />
        <span className="text-[#0F1F14] font-medium">{partner?.name}</span>
        <span className="text-[#4A5E51]">· RCA</span>
        {sede && <span className="text-[#4A5E51]">· {sede}</span>}
      </div>

      {/* Titolo modulo (visibile solo al primo step) */}
      {wiz.step === 1 && (
        <div className="border-b border-[#E5E2DB] pb-4">
          <h1 className="text-2xl sm:text-3xl font-semibold text-[#0F1F14]">
            Richiedi il tuo preventivo in convenzione
          </h1>
          <p className="text-sm text-[#4A5E51] mt-2">
            Una procedura guidata ti aiuterà a inviare solo le informazioni e i documenti necessari
            in base alla tua situazione. Il servizio è riservato ai dipendenti delle aziende
            convenzionate e agli appartenenti al loro stato di famiglia.
          </p>
        </div>
      )}

      {/* Render step corrente */}
      {currentStepKey === "step1_soggetto" && (
        <Step1SoggettoRichiesta state={wiz.state} setField={wiz.setField} />
      )}
      {currentStepKey === "step2_tipo" && (
        <Step2TipoRichiesta state={wiz.state} setField={wiz.setField} />
      )}
      {currentStepKey === "step3_documenti" && (
        <Step3Documenti
          state={wiz.state} setField={wiz.setField}
          docs={wiz.docs} requiredDocs={wiz.requiredDocs}
          setDoc={wiz.setDoc} removeDoc={wiz.removeDoc}
        />
      )}
      {currentStepKey === "step3b_cambio_veicolo" && (
        <Step3bCambioVeicolo
          state={wiz.state} setField={wiz.setField}
          docs={wiz.docs} requiredDocs={wiz.requiredDocs}
          setDoc={wiz.setDoc} removeDoc={wiz.removeDoc}
        />
      )}
      {currentStepKey === "step4_veicolo" && (
        <Step4Veicolo state={wiz.state} setField={wiz.setField} />
      )}
      {currentStepKey === "step5_bersani" && (
        <Step5Bersani
          state={wiz.state} setField={wiz.setField}
          docs={wiz.docs} setDoc={wiz.setDoc} removeDoc={wiz.removeDoc}
        />
      )}
      {currentStepKey === "step_garanzie" && (
        <StepGaranzie state={wiz.state} setField={wiz.setField} />
      )}
      {currentStepKey === "step6_contatto" && (
        <Step6Contatto
          state={wiz.state} setField={wiz.setField}
          minimalContattoMode={wiz.state.tipo_richiesta === "contatto"}
        />
      )}
      {currentStepKey === "step7_riepilogo" && (
        <Step7Riepilogo
          state={wiz.state} setField={wiz.setField}
          partner={partner}
          docs={wiz.docs}
          requiredDocs={wiz.requiredDocs}
          missingRequiredDocs={wiz.missingRequiredDocs}
          submitting={submitting}
          submitError={submitError}
          onSubmit={handleSubmit}
        />
      )}

      {/* HOTFIX 27-lug — Box blocker: se Avanti disabilitato, mostra lista campi/doc mancanti */}
      {currentStepKey !== "step7_riepilogo" && !canGoNext() && (
        <RcaBlockersBox state={wiz.state} step={currentStepKey}
                          missingDocs={wiz.missingRequiredDocs} />
      )}

      {/* Nav buttons */}
      {currentStepKey !== "step7_riepilogo" && (
        <div className="flex items-center justify-between gap-3 pt-4 border-t border-[#E5E2DB]">
          <button
            type="button"
            onClick={goBack}
            data-testid="rca-btn-back"
            className="inline-flex items-center gap-1 px-4 py-2 rounded-full text-sm font-medium transition-all text-[#4A5E51] border border-[#E5E2DB] bg-white hover:border-[#4A5E51]"
          >
            <ChevronLeft className="w-4 h-4" /> {wiz.step <= 1 ? "Cambia tipo" : "Indietro"}
          </button>
          <button
            type="button"
            onClick={goNext}
            disabled={!canGoNext()}
            data-testid="rca-btn-next"
            className={`inline-flex items-center gap-1 px-5 py-2 rounded-full text-sm font-semibold transition-all ${
              canGoNext()
                ? "bg-[var(--brand-accent,#163321)] text-white hover:opacity-90 shadow-md"
                : "bg-gray-300 text-white cursor-not-allowed"
            }`}
          >
            Avanti <ChevronRight className="w-4 h-4" />
          </button>
        </div>
      )}
    </div>
  );
}


// ═══════════════════════════════════════════════════════════════════════
// HOTFIX 27-lug — Box "blocker" con lista chiara di cosa manca
// ═══════════════════════════════════════════════════════════════════════
const DOC_LABELS = {
  documento_identita_dipendente: "Documento identità dipendente",
  badge_dipendente: "Badge dipendente",
  busta_paga_dipendente: "Busta paga dipendente",
  stato_famiglia: "Stato di famiglia",
  documento_identita_stesso_nucleo_familiare: "Documento identità familiare",
  libretto_veicolo_da_assicurare: "Libretto veicolo da assicurare",
  libretto_veicolo_classe_merito: "Libretto veicolo donante (classe di merito)",
  attestato_rischio_disponibile: "Attestato di rischio",
  documento_vendita_rottamazione: "Documento vendita/rottamazione",
  libretto_nuovo_veicolo: "Libretto nuovo veicolo",
  iban_rimborso: "IBAN per rimborso",
};

function _collectFormBlockers(step, s) {
  const b = [];
  if (step === "step1_soggetto" && !s.soggetto_richiesta) {
    b.push("Seleziona chi effettua la richiesta");
  }
  if (step === "step2_tipo") {
    if (!s.tipo_richiesta) b.push("Seleziona il tipo di richiesta");
    if (s.tipo_richiesta === "cambio_veicolo" && !s.sub_operazione)
      b.push("Seleziona se cambio veicolo o vendita/rottamazione");
  }
  if (step === "step3b_cambio_veicolo") {
    const t = (s.targa_veicolo_cambiato || "").toUpperCase();
    if (!/^[A-Z]{2}\d{3}[A-Z]{2}$/.test(t))
      b.push("Targa vecchio veicolo (formato: AB123CD)");
  }
  if (step === "step3_documenti" && s.soggetto_richiesta === "familiare") {
    const t = s.dipendente_titolare || {};
    if (!t.nome) b.push("Nome dipendente titolare");
    if (!t.cognome) b.push("Cognome dipendente titolare");
    if (!t.parentela) b.push("Parentela con il dipendente titolare");
    if (!/^[A-Z0-9]{11,16}$/.test((t.codice_fiscale || "").toUpperCase()))
      b.push("Codice fiscale del dipendente titolare (11-16 caratteri)");
  }
  if (step === "step4_veicolo") {
    if (!s.veicolo?.targa) b.push("Targa del veicolo da assicurare");
    if (!s.veicolo?.tipo_veicolo) b.push("Tipo veicolo (auto/moto/…)");
  }
  if (step === "step5_bersani") {
    // v1.75.2 — Nuova domanda dedicata precede RC Familiare (solo tipo_richiesta="nuova")
    if (s.tipo_richiesta === "nuova" && !s.veicolo_ha_classe_merito) {
      b.push("Indica se il veicolo è già assicurato con una classe di merito");
    }
    if (!s.rc_familiare) b.push("Rispondi se vuoi verificare RC Familiare");
    else if (s.rc_familiare === "si") {
      if (s.sinistri_5y_donante === true)
        b.push("Con sinistri con colpa non è possibile procedere — scegli 'continua senza RC Familiare'");
      if (s.sinistri_5y_donante === null || s.sinistri_5y_donante === undefined)
        b.push("Indica se ci sono stati sinistri con colpa negli ultimi 5 anni");
      if (s.sinistri_5y_donante === false) {
        if (!s.intestatario_donante) b.push("Seleziona chi è l'intestatario del veicolo donante");
        const d = s.donante_info || {};
        if (!d.nome) b.push("Nome intestatario donante");
        if (!d.cognome) b.push("Cognome intestatario donante");
        if (!d.targa) b.push("Targa veicolo donante");
        if (s.intestatario_donante === "familiare" && !d.parentela)
          b.push("Parentela con l'intestatario donante");
      }
    }
  }
  if (step === "step6_contatto") {
    const c = s.contatto || {};
    if (!c.nome) b.push("Nome contatto");
    if (!c.cognome) b.push("Cognome contatto");
    if (!/^[+\d\s]{6,30}$/.test(c.telefono || "")) b.push("Telefono valido (6-30 caratteri)");
    if (!s.contatto_preferito) b.push("Canale di contatto preferito");
    if (s.tipo_richiesta !== "contatto") {
      if (s.contatto_preferito === "email" && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(c.email || ""))
        b.push("Email valida");
      if (!/^[A-Z0-9]{11,16}$/.test((c.codice_fiscale || "").toUpperCase()))
        b.push("Codice fiscale contraente");
    }
    if (s.contatto_preferito === "chiamata") {
      if (!s.contatto_dettagli?.giorno) b.push("Giorno preferito per la chiamata");
      if (!s.contatto_dettagli?.fascia_oraria) b.push("Fascia oraria per la chiamata");
    }
  }
  return b;
}

function RcaBlockersBox({ state, step, missingDocs }) {
  const formBlockers = _collectFormBlockers(step, state);
  const docBlockers = (missingDocs || []).map(id => DOC_LABELS[id] || id);
  const all = [...formBlockers, ...docBlockers];
  if (all.length === 0) return null;
  return (
    <div className="rounded-2xl border-2 border-amber-300 bg-amber-50 p-4 mb-4"
          data-testid="rca-submit-blockers">
      <div className="flex items-start gap-2 mb-2">
        <AlertTriangle className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" />
        <div>
          <p className="font-semibold text-amber-900 text-sm">
            Per procedere completa questi elementi:
          </p>
          <ul className="mt-1.5 text-sm text-amber-900 space-y-0.5 list-disc list-inside">
            {all.map((label, i) => (
              <li key={i} data-testid={`rca-blocker-item-${i}`}>{label}</li>
            ))}
          </ul>
        </div>
      </div>
    </div>
  );
}
